import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useOrg } from "../org";
import { Initials, displayName } from "../people/bits";
import { ContactActions, useLogCare } from "./LogCare";
import { AssignedToMe } from "./Assignments";
import { TodayMoments } from "./LifeMoments";
import { churchHour, dayLabel, dueText, fmtInChurch, lastText, methodLabel } from "./text";

const partOfDay = () => { const h = churchHour(); return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"; };

const AttentionCard = ({ it, i }) => {
  const navigate = useNavigate();
  const p = it.person;
  const tone = it.overdue_days > 0 ? "status-overdue" : "status-attention";
  return (
    <motion.div className="py-7 border-b border-line" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.06 * i } }} data-testid={`attention-card-${p.id}`}>
      <div className={`thread ${it.kind === "rhythm" ? "thread-active" : ""}`}>
        <button className="flex gap-5 text-left w-full group" onClick={() => navigate(`/people/${p.id}`)} data-testid={`open-person-${p.id}`}>
          <Initials p={p} size={64} />
          <div className="min-w-0 flex-1">
            <h3 className="t-name-lg group-hover:text-brand transition-colors">{displayName(p)}</h3>
            {it.why && <p className="t-body text-[17px] mt-1.5">{it.why}</p>}
            <p className="t-meta mt-1">{lastText(p.last_connection)}</p>
            <p className={`status ${tone} mt-3`} data-testid={`attention-due-${p.id}`}>{dueText(it)}</p>
          </div>
        </button>
        <ContactActions p={p} careNeedId={it.care_need_id} className="mt-5" />
      </div>
    </motion.div>
  );
};

const MomentCard = ({ it }) => {
  const navigate = useNavigate();
  const openLog = useLogCare();
  const p = it.person;
  return (
    <div className="tint p-5 thread thread-rhythm" style={{ paddingLeft: 26 }} data-testid={`moment-card-${p.id}`}>
      <button className="flex gap-4 text-left w-full" onClick={() => navigate(`/people/${p.id}`)} data-testid={`open-person-moment-${p.id}`}>
        <Initials p={p} size={52} />
        <div className="min-w-0"><p className="t-name">{displayName(p)}</p>{it.why && <p className="t-body mt-1">{it.why}</p>}<p className="t-meta mt-0.5">{lastText(p.last_connection)}</p></div>
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 sm:pl-[68px]">
        <span className="status status-rhythm" data-testid={`moment-due-${p.id}`}>{dueText(it)}</span>
        <button onClick={() => openLog(p, { careNeedId: it.care_need_id })} className="btn btn-outline btn-sm bg-surface" data-testid={`moment-log-${p.id}`}>Log care</button>
      </div>
    </div>
  );
};

const Section = ({ title, hint, children, testid }) => (
  <section className="mt-14" data-testid={testid}><h2 className="t-heading">{title}</h2>{hint && <p className="t-meta mt-1">{hint}</p>}<div className="mt-4">{children}</div></section>
);

export default function Today() {
  const { api, me, membership, onTenantError } = useOrg();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = useCallback(() => api("/today").then(setD).catch((e) => { onTenantError(e); toast.error(e.message); }), [api]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); const on = () => load(); window.addEventListener("care:logged", on); return () => window.removeEventListener("care:logged", on); }, [load]);
  const first = me.user.name.split(" ")[0];
  const n = d?.attention.length || 0;
  return (
    <div className="max-w-3xl" data-testid="today-screen">
      <p className="t-label" data-testid="today-date">{fmtInChurch(new Date().toISOString(), { weekday: "long", month: "long", day: "numeric" })} · {membership.org_name}</p>
      <h1 className="t-greeting mt-3" data-testid="today-greeting">Good {partOfDay()}, {first}.</h1>
      {d && membership.role === "leader" && !d.people_count && !d.assigned?.length ? (
        <div className="mt-8 p-6 rounded-[16px] bg-[color:var(--canvas)]" data-testid="today-leader-empty">
          <p className="t-name">You're all set.</p>
          <p className="t-body mt-2 text-ink-soft">Once your care team assigns people or care responsibilities to you, they'll appear here.</p>
        </div>
      ) : d && (
        <p className="t-lead mt-4" data-testid="today-summary">
          {n ? <>You have <span className="text-ink font-semibold">{n} {n === 1 ? "person" : "people"}</span> who {n === 1 ? "needs" : "need"} your attention today.</> : d.moments.length ? <>No one needs your attention today. A few people would welcome a check-in when you have a moment.</> : <>No one needs your attention today. Everyone assigned to you is currently within their Care Rhythm.</>}
        </p>
      )}
      {!d && <p className="t-meta mt-4">Loading…</p>}
      {d?.assigned?.length > 0 && <AssignedToMe items={d.assigned} onChange={load} />}
      {n > 0 && <div className="mt-6 border-t border-line" data-testid="today-attention">{d.attention.map((it, i) => <AttentionCard key={it.person.id} it={it} i={i} />)}</div>}
      {d?.moments.length > 0 && (
        <Section title="When you have a moment" hint="Outside their usual Care Rhythm" testid="today-moments">
          <div className="space-y-3">{d.moments.map((it) => <MomentCard key={it.person.id} it={it} />)}</div>
        </Section>
      )}
      {d?.cared_today.length > 0 && (
        <Section title="Cared for today" testid="today-done">
          {d.cared_today.map((c) => (
            <div key={c.person_id} className="flex items-center gap-4 py-3" data-testid={`done-row-${c.person_id}`}>
              <div className="relative"><Initials p={c} size={44} /><span className="absolute -right-1 -bottom-1 h-6 w-6 rounded-full bg-brand text-white flex items-center justify-center ring-2 ring-paper"><Check size={14} strokeWidth={3} /></span></div>
              <div className="min-w-0"><p className="t-name-sm">{c.first_name} {c.last_name}</p><p className="t-meta">{methodLabel(c.method)} · {c.next_on ? `Next check-in ${dayLabel(c.next_on)}` : "No follow-up scheduled"}</p></div>
            </div>
          ))}
        </Section>
      )}
      {d?.coming_up.length > 0 && (
        <Section title="Coming up this week" testid="today-coming">
          <div className="rule-list border-y border-line">
            {d.coming_up.map((it) => (
              <button key={it.person.id} onClick={() => navigate(`/people/${it.person.id}`)} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={`upcoming-${it.person.id}`}>
                <Initials p={it.person} size={44} />
                <div className="flex-1 min-w-0"><p className="t-name-sm group-hover:text-brand transition-colors">{displayName(it.person)}</p><p className="t-meta truncate">{it.reason}</p></div>
                <span className="text-sm font-medium text-ink-soft shrink-0">{dayLabel(it.due_on)}</span>
              </button>
            ))}
          </div>
        </Section>
      )}
      {d && <TodayMoments />}
    </div>
  );
}
